import { formatOrderType } from "@/vybe-trader-client/trade-history/types";
import type { Quote } from "@/vybe-trader-client/subscribed-symbols/types";
import { getTradeClosePrice } from "@/vybe-trader-client/opened-trades/utils";

/**
 * Interface for trade data needed for SL/TP calculations
 */
export interface TradeData {
  closePrice?: number;
  openPrice: number;
  orderType: number;
  digits?: number;
}

/**
 * Configuration for SL/TP calculations (legacy percentage helpers)
 */
export interface SLTPCalculationConfig {
  /** Price distance as a percentage (default: 0.0002 = 0.02%) */
  priceDistancePercent?: number;
  /** Minimum absolute price distance (default: 0.0002) */
  minPriceDistance?: number;
}

/** Bid/ask-aware defaults: BUY uses bid, SELL uses ask via getTradeClosePrice. */
export interface DefaultLevelsOptions {
  quote?: Pick<Quote, "bid" | "ask"> | null;
  /** When trade.digits is missing, callers may override */
  digits?: number;
  /** Broker-style points (default 15). Delta = points × 10^-digits */
  points?: number;
}

export type CalculateDefaultLevelsParams = SLTPCalculationConfig &
  DefaultLevelsOptions;

function resolveDigits(trade: TradeData, override?: number): number {
  if (
    typeof override === "number" &&
    Number.isFinite(override) &&
    override >= 0 &&
    override <= 16
  ) {
    return Math.floor(override);
  }
  const d = trade.digits;
  if (typeof d === "number" && Number.isFinite(d) && d >= 0 && d <= 16) {
    return Math.floor(d);
  }
  return 5;
}

/** Price delta for N points at symbol precision (MT-style point = 10^-digits). */
export function pointsToDelta(digits: number, points: number): number {
  return points * Math.pow(10, -digits);
}

function roundToDigits(price: number, digits: number): number {
  const f = Math.pow(10, digits);
  return Math.round(price * f) / f;
}

/**
 * Default SL/TP distance from the anchor price.
 *
 * With a live bid/ask quote the default follows market structure instead of a
 * fixed point count (which was far too tight on wide-spread symbols like gold):
 *   distance = max(3 × current spread, 0.05% of price), rounded to the
 * symbol's tick (10^-digits). An explicit `points` override, or a missing
 * quote, falls back to the legacy points × 10^-digits behaviour.
 */
function resolveDefaultDelta(
  trade: TradeData,
  params: CalculateDefaultLevelsParams,
  digits: number,
): number {
  const tick = Math.pow(10, -digits);
  if (typeof params.points === "number" && Number.isFinite(params.points)) {
    return pointsToDelta(digits, params.points);
  }

  const bid = params.quote?.bid;
  const ask = params.quote?.ask;
  if (
    typeof bid === "number" &&
    typeof ask === "number" &&
    bid > 0 &&
    ask > 0 &&
    ask >= bid
  ) {
    const spread = ask - bid;
    const price = getTradeClosePrice(trade, params.quote ?? undefined);
    const raw = Math.max(3 * spread, price * 0.0005);
    // Round to the symbol's tick, never below one tick.
    return Math.max(tick, Math.round(raw / tick) * tick);
  }

  // No quote — legacy fixed default.
  return pointsToDelta(digits, 15);
}

/**
 * Default Stop Loss: 15 points from anchor (mirror of TP anchor rules).
 */
export const calculateDefaultSlPrice = (
  trade: TradeData,
  params: CalculateDefaultLevelsParams = {},
): number => {
  if (!trade) return 0;

  const digits = resolveDigits(trade, params.digits);
  const delta = resolveDefaultDelta(trade, params, digits);
  const market = getTradeClosePrice(trade, params.quote ?? undefined);
  const open = trade.openPrice;
  const tradeType = formatOrderType(trade.orderType);
  const isBuy = tradeType.includes("Buy");

  let raw: number;
  if (isBuy) {
    const ref = market > open ? open : market;
    raw = ref - delta;
  } else {
    const ref = market < open ? open : market;
    raw = ref + delta;
  }

  return roundToDigits(raw, digits);
};

/**
 * Default Take Profit: 15 points from anchor (open vs market).
 */
export const calculateDefaultTpPrice = (
  trade: TradeData,
  params: CalculateDefaultLevelsParams = {},
): number => {
  if (!trade) return 0;

  const digits = resolveDigits(trade, params.digits);
  const delta = resolveDefaultDelta(trade, params, digits);
  const market = getTradeClosePrice(trade, params.quote ?? undefined);
  const open = trade.openPrice;
  const tradeType = formatOrderType(trade.orderType);
  const isBuy = tradeType.includes("Buy");

  let raw: number;
  if (isBuy) {
    const ref = market < open ? open : market;
    raw = ref + delta;
  } else {
    const ref = market > open ? open : market;
    raw = ref - delta;
  }

  return roundToDigits(raw, digits);
};

export const calculateDefaultSLTPPrices = (
  trade: TradeData,
  params: CalculateDefaultLevelsParams = {},
): { slPrice: number; tpPrice: number } => {
  return {
    slPrice: calculateDefaultSlPrice(trade, params),
    tpPrice: calculateDefaultTpPrice(trade, params),
  };
};

/**
 * Calculate price distance based on current price and configuration
 */
export const calculatePriceDistance = (
  currentPrice: number,
  config: SLTPCalculationConfig = {},
): number => {
  const {
    priceDistancePercent = 0.0002, // 0.02%
    minPriceDistance = 0.0002,
  } = config;

  return Math.max(currentPrice * priceDistancePercent, minPriceDistance);
};

export const isBuyOrder = (orderType: number): boolean => {
  const tradeType = formatOrderType(orderType);
  return tradeType.includes("Buy");
};

export const calculateProfitAtPrice = (
  trade: TradeData & {
    digits?: number;
    profit?: number;
    lots?: number;
    symbol?: string;
  },
  exitPrice: number,
  displayInPips: boolean = false,
): string => {
  if (!trade || !exitPrice) return displayInPips ? "0.0p" : "0.00";

  const openPrice = trade.openPrice;
  const isBuy = isBuyOrder(trade.orderType);

  const priceDiff = exitPrice - openPrice;

  if (displayInPips && trade.digits) {
    const pipValue = Math.pow(10, -(trade.digits - 1));

    let pips = priceDiff / pipValue;

    if (!isBuy) {
      pips = -pips;
    }

    return `${pips.toFixed(1)}p`;
  } else {
    let profit = priceDiff;

    if (!isBuy) {
      profit = -profit;
    }

    if (trade.lots) {
      profit *= trade.lots * 100000;
    }

    return profit.toFixed(2);
  }
};

export const calculatePipsAtPrice = (
  trade: TradeData & { digits?: number },
  exitPrice: number,
): number => {
  if (!trade || !exitPrice || !trade.digits) return 0;

  const openPrice = trade.openPrice;
  const isBuy = isBuyOrder(trade.orderType);

  const priceDiff = exitPrice - openPrice;

  const pipValue = Math.pow(10, -(trade.digits - 1));

  let pips = priceDiff / pipValue;

  if (!isBuy) {
    pips = -pips;
  }

  return pips;
};

export const validateSLTPPrices = (
  trade: TradeData,
  slPrice: number,
  tpPrice: number,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const currentPrice =
    trade.closePrice && trade.closePrice > 0
      ? trade.closePrice
      : trade.openPrice;
  const isBuy = isBuyOrder(trade.orderType);

  if (slPrice > 0) {
    if (isBuy && slPrice >= currentPrice) {
      errors.push("Stop Loss for BUY order must be below current price");
    } else if (!isBuy && slPrice <= currentPrice) {
      errors.push("Stop Loss for SELL order must be above current price");
    }
  }

  if (tpPrice > 0) {
    if (isBuy && tpPrice <= currentPrice) {
      errors.push("Take Profit for BUY order must be above current price");
    } else if (!isBuy && tpPrice >= currentPrice) {
      errors.push("Take Profit for SELL order must be below current price");
    }
  }

  if (slPrice > 0 && tpPrice > 0) {
    if (isBuy && slPrice >= tpPrice) {
      errors.push("Stop Loss must be below Take Profit for BUY orders");
    } else if (!isBuy && slPrice <= tpPrice) {
      errors.push("Stop Loss must be above Take Profit for SELL orders");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
